import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, MapPin, Phone } from 'lucide-react';
import { useGetSystemSettingsQuery } from '../../../features/dashboard/settingApi';

const FooterLinkList = ({ title, links }) => (
  <div>
    <h3 className="mb-3 text-lg tracking-wide text-white">{title}</h3>
    {/* <span className="mb-4 block h-0.5 w-10 rounded-full bg-[#38bdf8]" aria-hidden="true" /> */}
    <ul className="space-y-3">
      {links.map((link) => (
        <li key={link.path}>
          <Link
            to={link.path}
            className="text-base text-white transition-colors duration-200 hover:text-white hover:underline"
          >
            {link.name}
          </Link>
        </li>
      ))}
    </ul>
  </div>
);

const ContactItem = ({ icon: Icon, children, href }) => {
  const content = (
    <span className="flex items-start gap-3 text-base leading-relaxed text-white">
      <span className="text- mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/5 ring-1">
        <Icon className="h-4 w-4" strokeWidth={1.75} />
      </span>
      <span className="pt-1.5">{children}</span>
    </span>
  );

  if (href) {
    return (
      <a href={href} className="group transition-colors hover:text-white">
        {content}
      </a>
    );
  }

  return content;
};

const FacebookIcon = ({ className = 'h-4 w-4' }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M22 12c0-5.52-4.48-10-10-10S2 6.48 2 12c0 4.84 3.44 8.87 8 9.8V15H8v-3h2V9.5C10 7.57 11.57 6 13.5 6H16v3h-2c-.55 0-1 .45-1 1v2h3v3h-3v6.95c5.05-.5 9-4.76 9-9.95z" />
  </svg>
);

const InstagramIcon = ({ className = 'h-4 w-4' }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M7.8 2h8.4C19.4 2 22 4.6 22 7.8v8.4a5.8 5.8 0 0 1-5.8 5.8H7.8C4.6 22 2 19.4 2 16.2V7.8A5.8 5.8 0 0 1 7.8 2m-.2 2A3.6 3.6 0 0 0 4 7.6v8.8A3.6 3.6 0 0 0 7.6 20h8.8a3.6 3.6 0 0 0 3.6-3.6V7.6A3.6 3.6 0 0 0 16.4 4H7.6m9.65 1.5a1.25 1.25 0 1 1 0 2.5 1.25 1.25 0 0 1 0-2.5M12 7a5 5 0 1 1 0 10 5 5 0 0 1 0-10m0 2a3 3 0 1 0 0 6 3 3 0 0 0 0-6z" />
  </svg>
);

const XIcon = ({ className = 'h-4 w-4' }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const TikTokIcon = ({ className = 'h-4 w-4' }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 00-.79-.05A6.34 6.34 0 003.15 15.2a6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.34-6.34V8.93a8.16 8.16 0 004.76 1.52V7.01a4.85 4.85 0 01-1-.32z" />
  </svg>
);

const FooterTemplate = () => {
  const { t } = useTranslation();
  const { data: settingsResponse } = useGetSystemSettingsQuery();
  const settings = settingsResponse?.data || {};

  const currentYear = new Date().getFullYear();
  const address = t('footer.contact.address');
  const mapsAddressLink = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;

  const quickLinks = [
    { name: t('footer.quickLinks.home'), path: '/' },
    { name: t('footer.quickLinks.about'), path: '/about' },
    { name: t('footer.quickLinks.howItWorks'), path: '/how-it-works' },
    { name: t('footer.quickLinks.faq'), path: '/faq' },
    { name: t('footer.quickLinks.terms'), path: '/terms' },
    { name: t('footer.quickLinks.privacy'), path: '/privacy' },
    { name: t('footer.quickLinks.cookies'), path: '/cookie-policy' },
  ];

  const userLinks = [
    { name: t('footer.forUsers.register'), path: '/auth/verify-identity' },
    { name: t('footer.forUsers.login'), path: '/auth/login' },
    { name: t('footer.forUsers.findSurgeon'), path: '/surgeons' },
  ];

  const surgeonLinks = [
    { name: t('footer.forSurgeons.register'), path: '/auth/register' },
    { name: t('footer.forSurgeons.login'), path: '/auth/login' },
    { name: t('footer.forSurgeons.pricing'), path: '/auth/pricing' },
  ];

  const socialLinks = [
    settings.facebookUrl && {
      name: 'Facebook',
      url: settings.facebookUrl,
      icon: <FacebookIcon />,
    },
    settings.instagramUrl && {
      name: 'Instagram',
      url: settings.instagramUrl,
      icon: <InstagramIcon />,
    },
    settings.twitterUrl && {
      name: 'X',
      url: settings.twitterUrl,
      icon: <XIcon />,
    },
    settings.tiktokUrl && {
      name: 'TikTok',
      url: settings.tiktokUrl,
      icon: <TikTokIcon />,
    },
  ].filter(Boolean);

  return (
    <footer className="relative overflow-hidden bg-cyan-500 text-white">
      {/* <div
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(2,108,182,0.18),_transparent_55%)]"
        aria-hidden="true"
      /> */}
      <div
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_rgba(56,189,248,0.08),_transparent_50%)]"
        aria-hidden="true"
      />

      <div className="relative mx-auto max-w-7xl px-4 py-4 pt-10 sm:px-6 lg:px-8 lg:pt-16">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-10">
          <div className="lg:col-span-4">
            <Link to="/" className="inline-block">
              <img
                src="/image/logo-white.png"
                alt="TrustSurgery logo"
                loading="lazy"
                decoding="async"
                width="128"
                height="64"
                className="h-auto w-28 md:w-32"
              />
            </Link>
            <p className="mt-5 max-w-sm text-sm leading-7 text-white md:text-[15px]">
              {t('footer.tagline')}
            </p>
             {socialLinks.length > 0 && (
             <div className="flex items-center space-x-3">
            <span className="text-sm text-white md:text-lg">{t('footer.followLabel')}</span>
            {socialLinks.map((social) => (
              <a
                key={social.name}
                href={social.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex h-8 w-8 items-center justify-center rounded-full bg-white text-cyan-500 transition-all hover:bg-cyan-600 hover:text-white"
                aria-label={social.name}
              >
                {social.icon}
              </a>
            ))}
          </div>
             )}
          </div>

          <div className="grid grid-cols-1 gap-10 sm:grid-cols-3 lg:col-span-8 lg:gap-8">
            <FooterLinkList title={t('footer.quickLinks.heading')} links={quickLinks} />
            <FooterLinkList title={t('footer.forUsers.heading')} links={userLinks} />
            <FooterLinkList title={t('footer.forSurgeons.heading')} links={surgeonLinks} />
          </div>
        </div>

        <div className="pt-6">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3 md:gap-8">
            <ContactItem icon={MapPin} href={mapsAddressLink}>
              {address}
            </ContactItem>
            <ContactItem icon={Mail} href={`mailto:${settings.footerEmail || t('footer.contact.email')}`}>
              {settings.footerEmail || t('footer.contact.email')}
            </ContactItem>
            <ContactItem icon={Phone} href={`tel:${settings.footerPhone || t('footer.contact.phone')}`}>
              {settings.footerPhone || t('footer.contact.phone')}
            </ContactItem>
          </div>
         
        </div>

        <div className="mt-6 border-t border-cyan-400 pt-6">
          <p className="text-center text-xs text-white">
            {t('footer.copyright', { year: currentYear })}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default FooterTemplate;
